// InitializePricingDialog · Lot M3
// 2 champs uniquement : asking_price + net_seller. Devise fixée EUR (V1).
// Aucun 3e prix (min_acceptable, etc.) — modèle strict à 2 prix.

import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter }
  from "@/components/ui/dialog";
import { fmtMoney } from "@/lib/money";

const inputCls =
  "h-11 rounded-lg bg-white px-3 text-[15px] outline-none " +
  "focus:ring-2 focus:ring-[#C8FF2F]/40 font-mono";
const inputStyle = { boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" };

export default function InitializePricingDialog({ open, onOpenChange, onSubmit }) {
  const [asking, setAsking] = useState("");
  const [net, setNet] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const askingN = Number(asking);
  const netN = Number(net);
  const bothValid = askingN > 0 && netN > 0;
  const invariantOK = bothValid && askingN >= netN;
  const marge = useMemo(() => {
    if (!invariantOK) return null;
    return askingN - netN;
  }, [askingN, netN, invariantOK]);

  const submit = async (e) => {
    e.preventDefault();
    if (!bothValid) return toast.error("Prix affiché et net vendeur > 0 requis");
    if (!invariantOK) {
      return toast.error("Le prix affiché doit être ≥ au net vendeur");
    }
    setSubmitting(true);
    try {
      await onSubmit?.({ asking_price: askingN, net_seller: netN, currency: "EUR" });
      setAsking(""); setNet("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[540px]" data-testid="init-pricing-dialog">
        <DialogHeader>
          <DialogTitle className="text-[19px] font-semibold tracking-[-0.01em]">
            Initialiser le pricing
          </DialogTitle>
          <p className="text-[12.5px] text-black/50 max-w-[400px]">
            Ces deux valeurs sont figées comme référence initiale du mandat.
            Le <b>net vendeur</b> ne pourra plus être modifié directement
            par l&apos;agence par la suite (proposition vendeur M7 requise).
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="mt-3 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] uppercase tracking-[0.14em] text-black/55">
                Prix affiché <span className="text-[#EF4444]">*</span>
              </span>
              <input
                type="number" min={1} step={1}
                value={asking} onChange={(e) => setAsking(e.target.value)}
                placeholder="35000"
                className={inputCls} style={inputStyle}
                autoFocus
                data-testid="init-asking-input"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] uppercase tracking-[0.14em] text-black/55">
                Net vendeur <span className="text-[#EF4444]">*</span>
              </span>
              <input
                type="number" min={1} step={1}
                value={net} onChange={(e) => setNet(e.target.value)}
                placeholder="30000"
                className={inputCls} style={inputStyle}
                data-testid="init-net-input"
              />
            </label>
          </div>

          <div className="rounded-xl bg-black/[0.02] p-3 text-[12.5px]"
               data-testid="init-invariant-preview">
            {!bothValid && (
              <span className="text-black/40">
                Saisissez les deux montants pour vérifier la cohérence.
              </span>
            )}
            {bothValid && !invariantOK && (
              <span className="text-[#B91C1C]">
                ⚠ Prix affiché ({fmtMoney(askingN)}) &lt; Net vendeur ({fmtMoney(netN)}).
                Le prix affiché doit être ≥ au net vendeur.
              </span>
            )}
            {invariantOK && (
              <div className="flex items-center justify-between text-black/60">
                <span>Écart</span>
                <span className="font-mono text-black">
                  {fmtMoney(marge)}
                </span>
              </div>
            )}
          </div>

          <DialogFooter className="mt-2 gap-2 sm:justify-end">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-full bg-white px-4 py-2 text-[13px] font-medium text-black/70 hover:text-black"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
              data-testid="init-pricing-cancel-btn"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || !invariantOK}
              className="rounded-full bg-[#C8FF2F] px-5 py-2 text-[13px] font-medium text-black transition-transform hover:scale-[1.005] disabled:opacity-40 disabled:hover:scale-100"
              data-testid="init-pricing-submit-btn"
            >
              {submitting ? "Initialisation…" : "Initialiser le pricing"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
